<template>
  <div id="book">
    <pre v-if="debug">
      sectionsShow: {{ sectionsShow }}
    </pre>
    <div class="panels">
      <Sidebar v-bind="$attrs" v-model:sections-flights-is-on="sectionsFlightsIsOn" v-model:sections-hotels-is-on="sectionsHotelsIsOn" v-model:sections-cars-is-on="sectionsCarsIsOn" class="panel-left-12-rem" />
      <div class="panel-right-flex">
        <Flights v-if="sectionsFlightsIsOn === true" v-show="sectionsShow === 'flights'" ref="flightsRef" v-bind="$attrs" v-model:flights="flights" v-model:sections-flights-date-start="sectionsFlightsDateStart" v-model:sections-flights-date-end="sectionsFlightsDateEnd" v-model:sections-flights-description="sectionsFlightsDescription" v-model:sections-flights-destination="sectionsFlightsDestination" v-model:sections-flights-flights="sectionsFlightsFlights" v-model:sections-flights-is-complete="sectionsFlightsIsComplete" :is-self-book="isSelfBook" :is-premium="isPremium" :organization-id="organizationId" @back="back" @error="$emit('error', $event)" @scroll="$emit('scroll', $event)" />
        <Hotels v-if="sectionsHotelsIsOn === true" v-show="sectionsShow === 'hotels'" v-bind="$attrs" v-model:hotels="hotels" v-model:sections-hotels-date-start="sectionsHotelsDateStart" v-model:sections-hotels-date-end="sectionsHotelsDateEnd" v-model:sections-hotels-description="sectionsHotelsDescription" v-model:sections-hotels-destination="sectionsHotelsDestination" v-model:sections-hotels-hotels="sectionsHotelsHotels" v-model:sections-hotels-is-complete="sectionsHotelsIsComplete" :is-self-book="isSelfBook" :organization-id="organizationId" @back="back" @error="$emit('error', $event)" @scroll="$emit('scroll', $event)" @update:is-self-book="$emit('update:isSelfBook', $event)" />
        <Cars v-if="sectionsCarsIsOn === true" v-show="sectionsShow === 'cars'" v-bind="$attrs" v-model:cars="cars" v-model:sections-cars-date-start="sectionsCarsDateStart" v-model:sections-cars-date-end="sectionsCarsDateEnd" v-model:sections-cars-description="sectionsCarsDescription" v-model:sections-cars-destination="sectionsCarsDestination" v-model:sections-cars-is-complete="sectionsCarsIsComplete" :organization-id="organizationId" @back="back" @error="$emit('error', $event)" @scroll="$emit('scroll', $event)" />
        <Travelers v-show="sectionsShow === 'travelers'" v-bind="$attrs" v-model:sections-travelers-description="sectionsTravelersDescription" v-model:sections-travelers-ids="sectionsTravelersIds" v-model:sections-travelers-is-complete="sectionsTravelersIsComplete" :is-self-book="isSelfBook" @back="back" @error="$emit('error', $event)" />
        <BookSummary v-if="sectionsShow === 'summary'" ref="bookSummaryRef" v-bind="$attrs" :is-self-book="isSelfBook" :cars="cars" :flights="flights" :hotels="hotels" @back="back" @submit="submit" />
        <div v-if="sectionsShow === 'none'">Please select an item to book from the check boxes above.</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Sidebar from "./TripNewBookGeneralSidebar.vue";
import Flights from "./TripNewBookFlights.vue";
import Hotels from "./TripNewBookHotels.vue";
import Cars from "./TripNewBookCars.vue";
import Travelers from "./TripNewBookTravelers.vue";
import BookSummary from "./TripNewBookSummary.vue";
import DateTime from "../utils/datetime";
import Trip from "../classes/trip.js";

const { utilDateISO8601 } = DateTime();

const props = defineProps(["isSelfBook", "isPremium", "organizationId", "debug"]);
const emit = defineEmits(["createTrip", "scroll", "error", "update:isSelfBook"]);

const bookSummaryRef = ref(null);

// Reactive variables
const flights = ref(null);
const hotels = ref(null);
const cars = ref(null);

// Cars section
const sectionsCarsDateStart = ref(null);
const sectionsCarsDateEnd = ref(null);
const sectionsCarsDescription = ref(null);
const sectionsCarsDestination = ref(null);
const sectionsCarsIsComplete = ref(false);
const sectionsCarsIsOn = ref(false);

// Flights section
const flightsRef = ref(null);
const sectionsFlightsDateStart = ref(null);
const sectionsFlightsDateEnd = ref(null);
const sectionsFlightsDescription = ref(null);
const sectionsFlightsDestination = ref(null);
const sectionsFlightsFlights = ref([]);
const sectionsFlightsIsComplete = ref(false);
const sectionsFlightsIsOn = ref(true);

// Hotels section
const sectionsHotelsDateStart = ref(null);
const sectionsHotelsDateEnd = ref(null);
const sectionsHotelsDescription = ref(null);
const sectionsHotelsDestination = ref(null);
const sectionsHotelsHotels = ref([]);
const sectionsHotelsIsComplete = ref(false);
const sectionsHotelsIsOn = ref(true);

// Travelers section
const sectionsTravelersDescription = ref(null);
const sectionsTravelersIds = ref(null);
const sectionsTravelersIsComplete = ref(false);

// Computed properties
const sectionsShow = computed(() => {
  if (sectionsFlightsIsOn.value === true && sectionsFlightsIsComplete.value === false) {
    return "flights";
  } else if (sectionsHotelsIsOn.value === true && sectionsHotelsIsComplete.value === false) {
    return "hotels";
  } else if (sectionsCarsIsOn.value === true && sectionsCarsIsComplete.value === false) {
    return "cars";
  } else if ((sectionsFlightsIsComplete.value === true || sectionsHotelsIsComplete.value === true || sectionsCarsIsComplete.value === true) && sectionsTravelersIsComplete.value !== true) {
    return "travelers";
  } else if ((sectionsFlightsIsComplete.value === true || sectionsHotelsIsComplete.value === true || sectionsCarsIsComplete.value === true) && sectionsTravelersIsComplete.value === true) {
    return "summary";
  } else {
    return "none";
  }
});

const tripDateStart = computed(() => {
  let dt;
  if (sectionsFlightsIsOn.value === true) {
    dt = sectionsFlightsDateStart.value;
  }
  if (sectionsHotelsIsOn.value === true) {
    if (sectionsFlightsIsOn.value === false || sectionsHotelsDateStart.value < dt) {
      dt = sectionsHotelsDateStart.value;
    }
  }
  if (sectionsCarsIsOn.value === true) {
    if ((sectionsFlightsIsOn.value === false && sectionsHotelsIsOn.value === false) || sectionsCarsDateStart.value < dt) {
      dt = sectionsCarsDateStart.value;
    }
  }
  return dt ? dt : utilDateISO8601(new Date());
});

const tripDateEnd = computed(() => {
  let dt;
  if (sectionsFlightsIsOn.value === true) {
    dt = sectionsFlightsDateEnd.value;
  }
  if (sectionsHotelsIsOn.value === true) {
    if (sectionsFlightsIsOn.value === false || sectionsHotelsDateEnd.value < dt) {
      dt = sectionsHotelsDateEnd.value;
    }
  }
  if (sectionsCarsIsOn.value === true) {
    if ((sectionsFlightsIsOn.value === false && sectionsHotelsIsOn.value === false) || sectionsCarsDateEnd.value < dt) {
      dt = sectionsCarsDateEnd.value;
    }
  }
  return dt ? dt : utilDateISO8601(new Date());
});

const tripDescription = computed(() => {
  let tripDescription = "Trip Request Information";
  if (sectionsTravelersIds.value.length > 0) {
    tripDescription += sectionsTravelersDescription.value;
  }
  if (sectionsFlightsIsOn.value === true) {
    tripDescription += sectionsFlightsDescription.value;
  }
  if (sectionsHotelsIsOn.value === true) {
    tripDescription += sectionsHotelsDescription.value;
  }
  if (sectionsCarsIsOn.value === true) {
    tripDescription += sectionsCarsDescription.value;
  }
  return tripDescription;
});

const tripDestination = computed(() => {
  if (sectionsFlightsIsOn.value === true) {
    return sectionsFlightsDestination.value;
  }
  if (sectionsHotelsIsOn.value === true) {
    return sectionsHotelsDestination.value;
  }
  if (sectionsCarsIsOn.value === true) {
    return sectionsCarsDestination.value;
  }
  return "Unknown";
});

// Watchers
watch(sectionsFlightsIsOn, val => {
  if (val === false) resetFlights();
});
watch(sectionsHotelsIsOn, val => {
  if (val === false) resetHotels();
});
watch(sectionsCarsIsOn, val => {
  if (val === false) resetCars();
});

// Methods
const back = () => {
  if (sectionsHotelsIsOn.value && !sectionsHotelsIsComplete.value) {
    if (sectionsFlightsIsOn.value) sectionsFlightsIsComplete.value = false;
  } else if (sectionsCarsIsOn.value && !sectionsCarsIsComplete.value) {
    if (sectionsHotelsIsOn.value) sectionsHotelsIsComplete.value = false;
    else if (sectionsFlightsIsOn.value) sectionsFlightsIsComplete.value = false;
  } else if (!sectionsTravelersIsComplete.value) {
    if (sectionsCarsIsOn.value) sectionsCarsIsComplete.value = false;
    else if (sectionsHotelsIsOn.value) sectionsHotelsIsComplete.value = false;
    else if (sectionsFlightsIsOn.value) sectionsFlightsIsComplete.value = false;
  } else sectionsTravelersIsComplete.value = false;
};

const resetFlights = () => {
  sectionsFlightsFlights.value = [];
  sectionsFlightsIsComplete.value = false;
  flightsRef.value?.resetToSearch();
};

const resetHotels = () => {
  hotels.value = null;
  sectionsHotelsDateStart.value = null;
  sectionsHotelsDateEnd.value = null;
  sectionsHotelsDescription.value = null;
  sectionsHotelsDestination.value = null;
  sectionsHotelsHotels.value = [];
  sectionsHotelsIsComplete.value = false;
};

const resetCars = () => {
  cars.value = null;
  sectionsCarsDateStart.value = null;
  sectionsCarsDateEnd.value = null;
  sectionsCarsDescription.value = null;
  sectionsCarsDestination.value = null;
  sectionsCarsIsComplete.value = false;
};

const submit = () => {
  const trip = new Trip();
  trip.description = tripDescription.value;
  trip.destination = tripDestination.value;
  trip.dateStart = tripDateStart.value;
  trip.dateEnd = tripDateEnd.value;
  trip.flights = sectionsFlightsFlights.value;
  trip.properties = sectionsHotelsHotels.value;
  trip.travelers = sectionsTravelersIds.value;
  trip.isSelfBook = props.isSelfBook;
  emit("createTrip", trip);
};

defineExpose({ resetFlights, resetLoading: () => { bookSummaryRef.value?.resetLoading(); } });
</script>

<style lang="less">
#new {
  #navigation {
    align-items: center;

    a {
      float: right;
      margin-left: 2rem;
    }

    a:last-child {
      text-align: right;
    }

    a:only-child {
      text-align: center;
    }

    button {
      margin-right: 1rem;
    }

    button.loading {
      background: @light;

      &:after {
        background: none;
      }
    }
  }
}
</style>
